import React, { useEffect, useRef, useState } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import { FilterProps } from './types';
import { useOnOutsideClick } from '../tools/outsideclick.hook';
import { DataType, Entity, FormatHint } from '../types';
import { Formatting } from '../data/formatting';

function formatMonthYear(date: Date): string {
  return Formatting.format(date, { dataType: DataType.Date, formatHint: FormatHint.MonthYear });
}

function formatMonth(date: Date): string {
  return Formatting.format(date, { dataType: DataType.Date, formatHint: FormatHint.Month });
}

function formatYear(year: number): string {
  return Formatting.format(new Date(new Date().setFullYear(year)), {
    dataType: DataType.Date,
    formatHint: FormatHint.Year,
  });
}

export function parseDateAsValue(date?: Date): Entity {
  if (!date) {
    return {
      value: '',
      label: '',
    };
  }

  return {
    value: `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(
      2,
      '0'
    )}`,
    label: formatMonthYear(date),
  };
}

function parseValueAsDate(value?: string) {
  if (!value) {
    return;
  }
  const year = value.substring(0, 4);
  const month = value.substring(4, 6);
  const date = value.substring(6, 8);
  return new Date(Number(year), Number(month) - 1, Number(date));
}

export const getDateRangeLabel = (startDateLabel: string, endDateLabel: string) => {
  if (!startDateLabel) {
    return `before ${endDateLabel}`;
  } else if (!endDateLabel) {
    return `after ${startDateLabel}`;
  }
  return `${startDateLabel}-${endDateLabel}`;
};

function DateRangeFilter({ definition: { name, label, min, max }, values = [], disabled, onChange }: FilterProps) {
  const minDate = min ? new Date(min) : undefined;
  const maxDate = max ? new Date(max) : undefined;

  const initialStart = parseValueAsDate(values[0]?.value.split('-')[0]);
  const initialEnd = parseValueAsDate(values[0]?.value.split('-')[1]);
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);

  useEffect(
    function handleClearClicked() {
      if (!values.length) {
        setStart(undefined);
        setEnd(undefined);
      }
    },
    [values.length]
  );

  const handleStartChange = (month: number, year: number) => {
    const date = new Date(year, month, 1, 12);
    setStart(date);
    if (date > end) {
      setEnd(undefined);
      checkAndUpdate(date, undefined);
    } else {
      checkAndUpdate(date, end);
    }
  };

  const handleEndChange = (month: number, year: number) => {
    const date = new Date(year, month + 1, 0, 12);
    setEnd(date);
    if (date < start) {
      setStart(undefined);
      checkAndUpdate(undefined, date);
    } else {
      checkAndUpdate(start, date);
    }
  };

  const checkAndUpdate = (startDate?: Date, endDate?: Date) => {
    const startValue = parseDateAsValue(startDate);
    const endValue = parseDateAsValue(endDate);

    onChange(name, [
      {
        value: `${startValue.value}-${endValue.value}`,
        label: getDateRangeLabel(startValue.label, endValue.label),
      },
    ]);
  };

  const minDateTextHelper = minDate ? `Min: ${formatMonthYear(minDate)}` : undefined;
  const maxDateTextHelper = maxDate ? `Max: ${formatMonthYear(maxDate)}` : undefined;

  return (
    <div className="relative">
      <div className="text-md font-bold flex-grow text-heading pb-2">{label}</div>
      <div className="flex items-start gap-2">
        <MonthSelector
          value={start}
          onChange={handleStartChange}
          align="left"
          helperText={minDateTextHelper}
          disabled={disabled}
        />
        <span className="pt-2">—</span>
        <MonthSelector
          value={end}
          onChange={handleEndChange}
          align="right"
          helperText={maxDateTextHelper}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

type Props = {
  value?: Date;
  onChange: (month: number, year: number) => void;
  align: 'left' | 'right';
  helperText?: string;
  disabled: boolean;
};

const { IconButton } = actions;

function MonthSelector({ onChange, align, value, helperText, disabled }: Props) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [year, setYear] = useState<number>();
  const [month, setMonth] = useState<number>();

  const el = useRef<HTMLDivElement>(null);
  useOnOutsideClick(el, () => setExpanded(false));

  const today = new Date();
  const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const targetDate = value !== undefined ? new Date(value) : firstDayOfCurrentMonth;
  const stringValue = formatMonthYear(targetDate);

  const openMenu = () => {
    setYear(targetDate.getFullYear());
    setMonth(targetDate.getMonth());
    setExpanded(true);
  };

  const incrementYear = () => {
    if (year < firstDayOfCurrentMonth.getFullYear()) {
      setYear(year + 1);
    }
  };

  const decrementYear = () => {
    setYear(year - 1);
  };

  const applyMonth = (value: number) => {
    onChange(value, year);
    setExpanded(false);
  };

  const months: { value: number; label: string; selectable: boolean; current: boolean }[] = [];
  if (year !== undefined && month !== undefined) {
    months.push(...getMonths(value, targetDate, year));
  }

  return (
    <div className="relative w-full" ref={el}>
      <div className="flex items-center rounded border border-default border-b bg-white h-10 gap-1">
        <button
          className={`h-full w-full flex-grow nowrap ${
            disabled ? 'text-lighter bg-medium rounded cursor-not-allowed' : ''
          }`}
          data-test-id={`date-range-${align}`}
          onClick={openMenu}
          disabled={disabled}
        >
          {value ? stringValue : ''}
        </button>
      </div>
      <span className="text-lighter mt-1 text-sm text-lighter">{helperText}</span>
      {expanded && (
        <div
          className={`z-10 absolute bg-white rounded border-light border top-full mt-2 shadow-sm w-56 ${
            align === 'left' ? 'left-0' : 'right-0'
          }`}
          data-test-id="date-range-calendar"
        >
          <div className="flex items-center bg-light border-b border-light">
            <IconButton onClick={decrementYear} color="secondaryInteractive">
              <IconButton.icons.ChevronLeft />
            </IconButton>
            <span className="flex-grow text-center">{formatYear(year)}</span>
            <IconButton onClick={incrementYear} color="secondaryInteractive">
              <IconButton.icons.ChevronRight />
            </IconButton>
          </div>
          <div className="grid grid-cols-3">
            {months.map((month, idx) => (
              <button
                disabled={!month.selectable}
                key={`${idx}-${month.label}-${year}`}
                className={`block px-3 py-2 ${month.selectable ? 'hover:bg-medium' : 'text-lighter cursor-default'} ${
                  month.current ? 'text-white bg-brand hover:bg-brand' : ''
                }`}
                onClick={() => applyMonth(month.value)}
              >
                {month.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const getMonths = (value: Date, targetDate: Date, year: number) => {
  const months: { value: number; label: string; selectable: boolean; current: boolean }[] = Array(12)
    .fill(null)
    .map((_, idx) => {
      const date = new Date();
      const firstDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      firstDayOfCurrentMonth.setFullYear(year);
      firstDayOfCurrentMonth.setMonth(idx);
      return {
        label: formatMonth(firstDayOfCurrentMonth),
        value: idx,
        selectable:
          year < firstDayOfCurrentMonth.getFullYear() ||
          (year === firstDayOfCurrentMonth.getFullYear() && idx <= firstDayOfCurrentMonth.getMonth()),
        current:
          value &&
          firstDayOfCurrentMonth.getFullYear() === targetDate.getFullYear() &&
          firstDayOfCurrentMonth.getMonth() === targetDate.getMonth(),
      };
    });

  return months;
};

export { DateRangeFilter };
